import { useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '@hypevault/shared-types';

/**
 * Emote Wall — v2 port of v1's emote-wall.ejs.
 * Spawns floating emojis (and twitch emote URLs if provided) from chat messages.
 *
 * Config (all optional):
 *   maxEmotes: number              (default 30)
 *   emoteSize: px                  (default 48)
 *   emoteDuration: seconds         (default 6)
 *   emoteSpeed: 1-100              (default 50, reserved for tuning)
 *   showTwitchEmotes: boolean      (default true)
 *   showEmoji: boolean             (default true)
 *   direction: 'up'|'down'|'left'|'right'   (default 'up')
 *   opacity: 0-100                 (default 90)
 */

interface FloatingEmote {
  id: string;
  kind: 'emoji' | 'twitch';
  content: string; // emoji char or URL
  startX: number; // %
  startY: number; // %
  rotStart: number;
  rotEnd: number;
  size: number;
  duration: number; // ms
  travel: number; // px, signed by direction
  bornAt: number;
}

// Unicode emoji regex (covers most ranges) — single emoji chars only
const EMOJI_RE =
  /(?:\p{Extended_Pictographic}(?:‍\p{Extended_Pictographic})*️?|\p{Emoji_Presentation})/gu;

function extractEmotes(msg: ChatMessage): { kind: 'emoji' | 'twitch'; content: string }[] {
  const out: { kind: 'emoji' | 'twitch'; content: string }[] = [];
  const emojis = msg.message.match(EMOJI_RE);
  if (emojis) for (const e of emojis) out.push({ kind: 'emoji', content: e });
  return out;
}

let counter = 0;
const uid = () => `${Date.now()}_${counter++}`;

export function EmoteWallWidget({
  fields,
  latestChatMessage,
}: {
  fields: Record<string, unknown>;
  latestChatMessage: ChatMessage | null;
}) {
  const maxEmotes = Number(fields.maxEmotes ?? 30);
  const emoteSize = Number(fields.emoteSize ?? 48);
  const emoteDuration = Number(fields.emoteDuration ?? 6);
  const showTwitchEmotes = (fields.showTwitchEmotes as boolean | undefined) ?? true;
  const showEmoji = (fields.showEmoji as boolean | undefined) ?? true;
  const direction = (fields.direction as 'up' | 'down' | 'left' | 'right' | undefined) ?? 'up';
  const opacity = Number(fields.opacity ?? 90) / 100;

  const [emotes, setEmotes] = useState<FloatingEmote[]>([]);
  const lastId = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!latestChatMessage || latestChatMessage.id === lastId.current) return;
    lastId.current = latestChatMessage.id;
    const extracted = extractEmotes(latestChatMessage);
    if (extracted.length === 0) return;

    const isVert = direction === 'up' || direction === 'down';
    const travelSign = direction === 'up' || direction === 'left' ? -1 : 1;
    const containerSize = isVert
      ? (containerRef.current?.clientHeight ?? 600)
      : (containerRef.current?.clientWidth ?? 1000);

    setEmotes((prev) => {
      const next = [...prev];
      for (const e of extracted) {
        if (e.kind === 'emoji' && !showEmoji) continue;
        if (e.kind === 'twitch' && !showTwitchEmotes) continue;
        if (next.length >= maxEmotes) next.shift();
        const rotStart = (Math.random() - 0.5) * 20;
        const rotEnd = rotStart + (Math.random() - 0.5) * 30;
        const size = emoteSize * (0.7 + Math.random() * 0.6);
        const duration = emoteDuration * (0.85 + Math.random() * 0.3) * 1000;
        const travelPct = 0.4 + Math.random() * 0.2;
        const travel = travelSign * containerSize * travelPct;
        next.push({
          id: uid(),
          kind: e.kind,
          content: e.content,
          startX: isVert ? Math.random() * 100 : direction === 'right' ? 5 : 95,
          startY: !isVert ? Math.random() * 100 : direction === 'down' ? 5 : 95,
          rotStart,
          rotEnd,
          size,
          duration,
          travel,
          bornAt: Date.now(),
        });
      }
      return next;
    });
  }, [
    latestChatMessage,
    direction,
    emoteSize,
    emoteDuration,
    maxEmotes,
    showEmoji,
    showTwitchEmotes,
  ]);

  // Cull expired
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      setEmotes((prev) => prev.filter((e) => now - e.bornAt < e.duration));
    }, 500);
    return () => clearInterval(t);
  }, []);

  const isVert = direction === 'up' || direction === 'down';
  const animName = isVert ? 'ew-float-vert' : 'ew-float-horiz';

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {emotes.map((e) => (
        <div
          key={e.id}
          style={
            {
              position: 'absolute',
              left: `${e.startX}%`,
              top: `${e.startY}%`,
              ['--rot-start' as string]: `${e.rotStart}deg`,
              ['--rot-end' as string]: `${e.rotEnd}deg`,
              ['--travel' as string]: `${e.travel}px`,
              ['--emote-opacity' as string]: String(opacity),
              animation: `${animName} ${e.duration}ms linear forwards`,
              willChange: 'transform, opacity',
              fontSize: e.size,
              lineHeight: 1,
            } as React.CSSProperties
          }
        >
          {e.kind === 'twitch' ? (
            <img
              alt=""
              src={e.content}
              style={{ width: e.size, height: e.size, display: 'block' }}
            />
          ) : (
            <span>{e.content}</span>
          )}
        </div>
      ))}
      <style>{`
        @keyframes ew-float-vert {
          0% { transform: translateY(0) rotate(var(--rot-start)) scale(0); opacity: 0; }
          5% { transform: translateY(0) rotate(var(--rot-start)) scale(1); opacity: var(--emote-opacity, 0.9); }
          90% { opacity: var(--emote-opacity, 0.9); }
          100% { transform: translateY(var(--travel)) rotate(var(--rot-end)) scale(1); opacity: 0; }
        }
        @keyframes ew-float-horiz {
          0% { transform: translateX(0) rotate(var(--rot-start)) scale(0); opacity: 0; }
          5% { transform: translateX(0) rotate(var(--rot-start)) scale(1); opacity: var(--emote-opacity, 0.9); }
          90% { opacity: var(--emote-opacity, 0.9); }
          100% { transform: translateX(var(--travel)) rotate(var(--rot-end)) scale(1); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
